import { Button, Menu } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import type { MouseEvent, ReactNode } from 'react';
import classes from './SubmitActionSplitButton.module.css';

export type SubmitActionMenuItem = {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  disabledReason?: string;
  testId?: string;
};

type SubmitActionSplitButtonProps = {
  submitLabel: string;
  submitDisabled?: boolean;
  loading?: boolean;
  menuAriaLabel: string;
  actions: SubmitActionMenuItem[];
  submitButtonTestId?: string;
  menuButtonTestId?: string;
};

const SubmitActionSplitButton = ({
  submitLabel,
  submitDisabled = false,
  loading = false,
  menuAriaLabel,
  actions,
  submitButtonTestId,
  menuButtonTestId,
}: SubmitActionSplitButtonProps) => {
  const menuDisabled = loading || actions.length === 0;

  const handleActionClick =
    (action: SubmitActionMenuItem) => (event: MouseEvent<HTMLButtonElement>) => {
      if (action.disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      void action.onClick();
    };

  return (
    <div className={classes.root}>
      <Button
        className={classes.submitButton}
        data-testid={submitButtonTestId}
        type="submit"
        loading={loading}
        disabled={submitDisabled}>
        {submitLabel}
      </Button>
      <Menu position="top-end" withinPortal shadow="md">
        <Menu.Target>
          <Button
            className={classes.menuButton}
            data-testid={menuButtonTestId}
            type="button"
            aria-label={menuAriaLabel}
            disabled={menuDisabled}>
            <IconChevronDown size={16} />
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {actions.map((action) => (
            <Menu.Item
              key={action.key}
              data-testid={action.testId}
              leftSection={action.icon}
              disabled={action.disabled}
              aria-disabled={action.disabled ? 'true' : undefined}
              title={action.disabledReason}
              onClick={handleActionClick(action)}>
              {action.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </div>
  );
};

export default SubmitActionSplitButton;
